import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  styled,
  Paper,
  Dialog,
  TableContainer,
  TableBody,
  Table,
  TableRow,
  TableCell,
  TableHead,
} from '@mui/material';
import DialogTransition from '../DialogTransition';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { FullScreenDialogTitle } from '../../dialogs/FullScreenDialogTitle';
import { FullScreenDialogContent } from '../../dialogs/FullScreenDialogContent';
import type {
  GroupedTripsByDate,
  InvoiceRow,
} from '../../../data/models/invoice.model';
import type { Provider } from '../../../data/models/provider.model';
import phoneUtils from '../../../services/phoneUtils';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import * as invoiceSlice from '../../../data/invoice.slice';
import { useAppSelector } from '../../../hooks/useAppSelector';
import CollapsibleDataGrid from '../../tables/CollapsibleDataGrid';
import { financialUtils } from '../../../services/financialUtils';
import { TripLegStatusBC } from '../../../data/models/trip-leg-status.model';
import { DateTime } from 'luxon';

const PREFIX = 'GenerateInvoice';

const classes = {
  container: `${PREFIX}-container`,
  gridContainer: `${PREFIX}-gridContainer`,
  invoiceDetails: `${PREFIX}-invoiceDetails`,
  invoiceDetailsBox: `${PREFIX}-invoiceDetailsBox`,
  invoiceDetailsTitle: `${PREFIX}-invoiceDetailsTitle`,
  invoiceDetailsValue: `${PREFIX}-invoiceDetailsValue`,
  invoiceDetailsText: `${PREFIX}-invoiceDetailsText`,
  summaryContainer: `${PREFIX}-summaryContainer`,
  summaryTitle: `${PREFIX}-summaryTitle`,
  summaryTableContainer: `${PREFIX}-summaryTableContainer`,
  summaryTableHeader: `${PREFIX}-summaryTableHeader`,
  tableHeaderAttested: `${PREFIX}-tableHeaderAttested`,
  tableHeaderTotalTrips: `${PREFIX}-tableHeaderTotalTrips`,
  tableHeaderAmount: `${PREFIX}-tableHeaderAmount`,
  summaryHeader: `${PREFIX}-summaryHeader`,
  summaryContextContainer: `${PREFIX}-summaryContextContainer`,
  summaryContextTotal: `${PREFIX}-summaryContextTotal`,
  summaryContextAmount: `${PREFIX}-summaryContextAmount`,
  invoiceTableContainer: `${PREFIX}-invoiceTableContainer`,
  totalCalculation: `${PREFIX}-totalCalculation`,
  calculationText: `${PREFIX}-calculationText`,
  adjustmentContainer: `${PREFIX}-adjustmentContainer`,
  adjustmentTitle: `${PREFIX}-adjustmentTitle`,
  adjustmentTable: `${PREFIX}-adjustmentTable`,
  grid: `${PREFIX}-grid`,
  cellText: `${PREFIX}-cellText`,
  adjustmentTotal: `${PREFIX}-adjustmentTotal`,
  adjustmentRevisedTotal: `${PREFIX}-adjustmentRevisedTotal`,
  noBorder: `${PREFIX}-noBorder`,
};

const StyledDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.container}`]: {
    width: '100%',
    maxWidth: 1152,
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  [`& .${classes.gridContainer}`]: {
    padding: '40px 40px 48px 40px',
    color: 'rgba(1, 16, 18, 0.90)',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  [`& .${classes.invoiceDetails}`]: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderRadius: 8,
    background:
      'linear-gradient(0deg, rgba(1, 16, 18, 0.04) 0%, rgba(1, 16, 18, 0.04) 100%), #FFF',
    padding: '32px 40px',
  },
  [`& .${classes.invoiceDetailsBox}`]: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 270,

    ['& .MuiTypography-root']: {
      fontFamily: 'Source Sans Pro',
      fontStyle: 'normal',
    },
  },
  [`& .${classes.invoiceDetailsTitle}`]: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
  [`& .${classes.invoiceDetailsValue}`]: {
    fontSize: 16,
    fontWeight: 400,
    lineHeight: '150%',
    letterSpacing: '0.15px',
  },
  [`& .${classes.invoiceDetailsText}`]: {
    color: 'rgba(1, 16, 18, 0.60)',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.summaryContainer}`]: {
    margin: 'theme.spacing(2) 0 theme.spacing(2) 0',
    padding: 'theme.spacing(2) 0 0 theme.spacing(1)',
  },
  [`& .${classes.summaryTableContainer}`]: {
    display: 'flex',
  },
  [`& .${classes.summaryTitle}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    lineHeight: '32px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.summaryTableHeader}`]: {
    padding: '16px 0 16px 0',
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '24px',
    letterSpacing: '0.17px',
  },
  [`& .${classes.summaryTableHeader} .${classes.tableHeaderAttested}`]: {
    width: theme.spacing(17.25),
    textAlign: 'left',
  },
  [`& .${classes.summaryTableHeader} .${classes.tableHeaderTotalTrips}`]: {
    width: theme.spacing(27.125),
    textAlign: 'right',
  },
  [`& .${classes.summaryTableHeader} .${classes.tableHeaderAmount}`]: {
    width: theme.spacing(15.25),
    textAlign: 'right',
  },
  [`& .${classes.summaryContextContainer}`]: {
    display: 'flex',
    width: theme.spacing(59.625),
    flexDirection: 'row',
    paddingTop: '8px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
    justifyContent: 'space-between',
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: 14,
    fontStyle: 'normal',
    fontWeight: 600,
    lineHeight: '143%',
    letterSpacing: '0.17px',
  },
  [`& .${classes.summaryContextAmount}`]: {
    fontSize: 16,
  },
  [`& .${classes.invoiceTableContainer}`]: {
    padding: 'theme.spacing(2) 0 0 0',
    margin: 'theme.spacing(2) 0 0 0',
    display: 'flex',
    flexDirection: 'column',
    gap: 'theme.spacing(2)',
  },
  [`& .${classes.totalCalculation}`]: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 'theme.spacing(8.125)',
    paddingBottom: 'theme.spacing(6)',
  },
  [`& .${classes.calculationText}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Source Sans Pro',
    fontSize: '21px',
    fontStyle: 'normal',
    fontWeight: '600',
    lineHeight: '24px',
  },
  [`& .${classes.adjustmentContainer}`]: {
    marginTop: theme.spacing(3),
    width: theme.spacing(59.625),

    [`& .${classes.adjustmentTable}`]: {
      '& .MuiTableCell-root': {
        fontFamily: 'Source Sans Pro',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '143%',
        letterSpacing: '0.17px',
        padding: '18px 8px',

        [`&.${classes.adjustmentTotal}`]: {
          fontWeight: 600,
        },

        [`&.${classes.adjustmentRevisedTotal}`]: {
          fontSize: 16,
          fontWeight: 600,
          lineHeight: '150%',
          letterSpacing: '0.15px',
        },
      },

      '& .MuiTableHead-root': {
        '& .MuiTableCell-root': {
          lineHeight: '24px',
          fontWeight: 600,
        },
      },

      [`& .${classes.noBorder} .MuiTableCell-root`]: {
        borderBottom: 'none',
      },
    },
  },
  [`& .${classes.adjustmentTitle}`]: {
    color: 'rgba(1, 16, 18, 0.90)',
    fontFamily: 'Bitter',
    fontSize: 21,
    fontWeight: 700,
    lineHeight: '32px',
    letterSpacing: '0.15px',
  },
  [`& .${classes.grid}`]: {
    border: 'none',
    '.MuiDataGrid-columnHeaderTitle': {
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.17px',
    },

    '& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContent, & .MuiDataGrid-columnHeaderTitleContainerContent, & .MuiDataGrid-columnHeaderTitle':
      {
        width: '100%',
        display: 'flex',
        alignItems: 'center',
      },
    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-columnHeader--last': {
      paddingRight: 0,
    },

    [`& .MuiDataGrid-row`]: {
      cursor: 'pointer',
    },

    [`& .MuiDataGrid-columnHeader`]: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '24px',
      letterSpacing: '0.17px',
    },

    [`& .MuiDataGrid-cell`]: {
      display: 'flex',
      alignItems: 'center',
    },
    [`& .${classes.cellText}`]: {
      color: 'rgba(1, 16, 18, 0.90)',
      fontFamily: 'Source Sans Pro',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: '143%',
      letterSpacing: '0.17px',
    },
  },
}));
interface InvoiceDialogProps {
  open: boolean;
  invoice: InvoiceRow | null;
  provider: Provider;
  onDownloadInvoice: (providerInvoiceID: number, invoiceNumber: string) => void;
  onClose: () => void;
}

export default function InvoiceDialog({
  open,
  invoice,
  provider,
  onDownloadInvoice,
  onClose,
}: InvoiceDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const handleInvoiceDownload = () => {
    if (invoice) {
      onDownloadInvoice(invoice.id, invoice.invoiceNumber);
    }
  };

  useEffect(() => {
    if (invoice) {
      dispatch(invoiceSlice.fetchTripLegsByProviderInvoiceIDAsync(invoice.id));
    }
  }, [invoice]);

  const invoiceDetails = useAppSelector(invoiceSlice.selectInvoiceDetails);

  const tripLegs = invoiceDetails?.tripLegs;

  const adjustments = useMemo(() => {
    return (tripLegs || [])
      .map(({ tripLegReferenceID, tripLegAddOnLineItems }) =>
        (tripLegAddOnLineItems || []).map(
          ({
            lineItemDescription,
            lineItemAmount,
            lineItemQuantity,
            createdOnUTC,
          }) => ({
            date: DateTime.fromISO(createdOnUTC).toFormat('MM/dd/yyyy'),
            description: `Trip ${tripLegReferenceID} - ${lineItemDescription}`,
            amount:
              financialUtils.parsePrice(lineItemAmount) * lineItemQuantity,
          }),
        ),
      )
      .flat();
  }, [tripLegs]);

  const totalAdjustmentAmount = useMemo(() => {
    return adjustments.reduce((sum, adjustment) => sum + adjustment.amount, 0);
  }, [adjustments]);

  const providerInfo = invoiceDetails?.transportationProvider;

  const billFrom = {
    name: providerInfo?.transportationProviderName,
    ownerName:
      providerInfo?.billingAddress?.contactFirstName +
      ' ' +
      providerInfo?.billingAddress?.contactLastName,
    address:
      providerInfo?.billingAddress?.addressLine1 +
      ', ' +
      providerInfo?.billingAddress?.city +
      ', ' +
      providerInfo?.billingAddress?.stateCode +
      ', ' +
      providerInfo?.billingAddress?.zipCode,
    phone: phoneUtils.formatTenDigitPhone(
      providerInfo?.billingPhoneNumber?.phoneNumber ?? '',
    ),
  };

  const getFinalizedStatus = (logs?: { tripLegStatusBC: string }[]) => {
    const priority = [
      TripLegStatusBC.NoLoad,
      TripLegStatusBC.CanceledLate,
      TripLegStatusBC.Completed,
    ];
    const matched = priority
      .map((status) => logs?.find((log) => log.tripLegStatusBC === status))
      .find(Boolean);
    switch (matched?.tripLegStatusBC) {
      case TripLegStatusBC.NoLoad:
        return TripLegStatusBC.NoLoad;
      case TripLegStatusBC.CanceledLate:
        return TripLegStatusBC.CanceledLate;
      case TripLegStatusBC.Completed:
        return TripLegStatusBC.Completed;
      default:
        return '';
    }
  };

  const invoiceTripLegRows = useMemo(() => {
    const formatTime = (isoString: string | undefined) => {
      if (!isoString) return '';
      return DateTime.fromISO(isoString).toFormat('h:mm a');
    };

    const formatDate = (isoString: string) => {
      if (!isoString) return '';

      const date = DateTime.fromISO(isoString);
      return date.toFormat('MM/d/yyyy');
    };
    return (
      tripLegs
        ?.map((tripLeg) => {
          return {
            ...tripLeg,
            id: tripLeg?.tripLegID ?? 0,
            tripLegReferenceID: tripLeg?.tripLegReferenceID ?? '',
            tripLegID: tripLeg?.tripLegID ?? 0,
            passengerUUID:
              tripLeg?.passengerUUID ?? tripLeg?.passenger?.passengerUUID ?? '',
            pickupTime: tripLeg?.isWillCall
              ? formatDate(tripLeg?.appointmentDate ?? '')
              : formatTime(tripLeg?.pickUpDueTimeUTC ?? ''),
            isWillCall: tripLeg?.isWillCall,
            tripID: tripLeg?.tripLegReferenceID,
            pickupAddress: [
              tripLeg?.pickUpAddressLine1,
              tripLeg?.pickUpAddressLine2,
            ]
              .filter(Boolean)
              .join(', '),
            pickupCity: [
              tripLeg?.pickUpCity,
              tripLeg?.pickUpStateCode,
              tripLeg?.pickUpZipCode,
            ]
              .filter((part) => !!part)
              .join(', '),
            dropoffAddress: [
              tripLeg?.dropOffAddressLine1,
              tripLeg?.dropOffAddressLine2,
            ]
              .filter(Boolean)
              .join(', '),
            dropoffCity: [
              tripLeg?.dropOffCity,
              tripLeg?.dropOffStateCode,
              tripLeg?.dropOffZipCode,
            ]
              .filter((part) => !!part)
              .join(', '),
            onSite: formatTime(
              tripLeg?.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.Onsite,
              )?.createdOnUTC ?? '',
            ),
            onBoard: formatTime(
              tripLeg?.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.Onboard,
              )?.createdOnUTC ?? '',
            ),
            completed: formatTime(
              tripLeg?.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.Completed,
              )?.createdOnUTC ?? '',
            ),
            noLoad: formatTime(
              tripLeg?.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.NoLoad,
              )?.createdOnUTC ?? '',
            ),
            canceledLate: formatTime(
              tripLeg?.tripLegStatusLogs?.find(
                ({ tripLegStatusBC }) =>
                  tripLegStatusBC === TripLegStatusBC.CanceledLate,
              )?.createdOnUTC ?? '',
            ),
            memberName:
              tripLeg?.passenger?.passengerFirstName +
              ' ' +
              tripLeg?.passenger?.passengerLastName,
            memberId: tripLeg?.passenger?.passengerID,
            driver: tripLeg?.driver,
            driverName:
              tripLeg?.driver?.driverFirstName +
              ' ' +
              tripLeg?.driver?.driverLastName,
            distance: tripLeg?.invoiceDistanceMiles,
            price: tripLeg?.payableAmount?.toString() ?? '0',
            hasRequiredTimestamps: true,
            isException: tripLeg?.isException,
            tripLegStatus: getFinalizedStatus(tripLeg?.tripLegStatusLogs),
          };
        })
        .sort((a, b) => {
          return a.appointmentDate.localeCompare(b.appointmentDate);
        }) ?? []
    );
  }, [tripLegs]);

  const invoicableTripLegsByDate: GroupedTripsByDate[] = useMemo(() => {
    const groups: GroupedTripsByDate[] = [];

    for (const tripLeg of invoiceTripLegRows) {
      const date = DateTime.fromISO(tripLeg.appointmentDate)
        .setZone('utc')
        .toFormat('MM/d/yyyy');

      const lastGroup = groups[groups.length - 1];
      if (lastGroup?.date === date) {
        lastGroup.rows.push(tripLeg);
      } else {
        groups.push({ date, rows: [tripLeg] });
      }
    }

    return groups;
  }, [invoiceTripLegRows]);

  return (
    <>
      <StyledDialog
        fullScreen={true}
        open={open}
        onClose={onClose}
        TransitionComponent={DialogTransition}>
        <FullScreenDialogTitle
          subTitle={t('invoices.title')}
          title={invoice?.invoiceNumber ?? ''}
          close={onClose}
          showMenu={true}
          handleDownload={handleInvoiceDownload}
        />
        <FullScreenDialogContent>
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Box className={classes.container}>
              <Paper elevation={3}>
                <Box className={classes.gridContainer}>
                  <Box className={classes.invoiceDetails}>
                    <Box className={classes.invoiceDetailsBox}>
                      <Typography
                        variant="h6"
                        className={classes.invoiceDetailsTitle}>
                        {t('invoices.billFrom')}
                      </Typography>
                      <Typography
                        variant="h6"
                        className={classes.invoiceDetailsValue}>
                        {billFrom.name}
                      </Typography>
                      <Typography
                        variant="h6"
                        className={classes.invoiceDetailsText}>
                        {billFrom.ownerName}
                        <br />
                        {billFrom.phone}
                        <br />
                        {billFrom.address}
                      </Typography>
                    </Box>
                    <Box className={classes.invoiceDetailsBox}>
                      <Typography
                        variant="h6"
                        className={classes.invoiceDetailsTitle}>
                        {t('invoices.generated')}
                      </Typography>
                      <Typography
                        variant="h6"
                        className={classes.invoiceDetailsValue}>
                        {invoice?.paymentOn}
                      </Typography>
                    </Box>
                    <Box className={classes.invoiceDetailsBox}>
                      <Typography
                        variant="h6"
                        className={classes.invoiceDetailsTitle}>
                        {t('common.status')}
                      </Typography>
                      {invoice?.billingStatusID && (
                        <Typography
                          variant="h6"
                          className={classes.invoiceDetailsValue}>
                          {invoice?.billingStatusBC} - {invoice?.paymentOn}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Box className={classes.summaryContainer}>
                    <Box className={classes.summaryHeader}>
                      <Box>
                        <Typography className={classes.summaryTitle}>
                          {t('invoices.summary')}
                        </Typography>
                      </Box>
                      <Box className={classes.summaryTableContainer}>
                        <Box className={classes.summaryTableHeader}>
                          <Box className={classes.tableHeaderAttested}>
                            {t('invoices.tripsAttested')}
                          </Box>
                        </Box>
                        <Box className={classes.summaryTableHeader}>
                          <Box className={classes.tableHeaderTotalTrips}>
                            {invoice?.totalTripLegs}
                          </Box>
                        </Box>
                        <Box className={classes.summaryTableHeader}>
                          <Box className={classes.tableHeaderAmount}>
                            {invoice?.amount}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                    <Box className={classes.summaryContextContainer}>
                      <Box className={classes.summaryContextTotal}>
                        {t('invoices.total')}
                      </Box>
                      <Box className={classes.summaryContextAmount}>
                        {invoice?.amount}
                      </Box>
                    </Box>
                  </Box>
                  {adjustments.length > 0 ? (
                    <Box className={classes.adjustmentContainer}>
                      <Box>
                        <Typography className={classes.adjustmentTitle}>
                          {t('invoices.adjustments')}
                        </Typography>
                      </Box>
                      <Box>
                        <TableContainer className={classes.adjustmentTable}>
                          <Table>
                            <TableHead>
                              <TableRow className={classes.noBorder}>
                                <TableCell>{t('invoices.date')}</TableCell>
                                <TableCell>
                                  {t('invoices.description')}
                                </TableCell>
                                <TableCell align="right">
                                  {t('invoices.amount')}
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {adjustments.map((adjustment, index) => (
                                <TableRow key={index}>
                                  <TableCell>{adjustment.date}</TableCell>
                                  <TableCell>
                                    {adjustment.description}
                                  </TableCell>
                                  <TableCell align="right">
                                    {financialUtils.formatPrice(
                                      adjustment.amount,
                                    )}
                                  </TableCell>
                                </TableRow>
                              ))}
                              <TableRow>
                                <TableCell
                                  colSpan={2}
                                  className={classes.adjustmentTotal}>
                                  {t('invoices.total')}
                                </TableCell>
                                <TableCell align="right">
                                  {financialUtils.formatPrice(
                                    totalAdjustmentAmount,
                                  )}
                                </TableCell>
                              </TableRow>
                              <TableRow className={classes.noBorder}>
                                <TableCell
                                  colSpan={2}
                                  className={classes.adjustmentRevisedTotal}>
                                  {t('invoices.revisedTotal')}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  className={classes.adjustmentRevisedTotal}>
                                  {financialUtils.formatPrice(
                                    financialUtils.parsePrice(
                                      invoice?.amount ?? '0',
                                    ) + totalAdjustmentAmount,
                                  )}
                                </TableCell>
                              </TableRow>
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    </Box>
                  ) : null}
                  <Box className={classes.invoiceTableContainer}>
                    {invoicableTripLegsByDate.map(({ date, rows }) => {
                      const dateTotalPrice = rows.reduce((sum, trip) => {
                        const numericPrice = Number(
                          (trip.price ?? '0').toString().replace(/[$,]/g, ''),
                        );
                        return financialUtils.roundTo(sum + numericPrice, 2);
                      }, 0);

                      const dateTotalPriceFormatted =
                        financialUtils.formatPrice(dateTotalPrice);

                      return (
                        <CollapsibleDataGrid
                          key={date}
                          title={`${date}: ${
                            rows.length > 1
                              ? t('generateInvoice.calculatedTotalPlural', {
                                  numTrips: rows.length,
                                  total: dateTotalPriceFormatted,
                                })
                              : t('generateInvoice.calculatedTotalSingular', {
                                  numTrips: rows.length,
                                  total: dateTotalPriceFormatted,
                                })
                          }`}
                          rows={rows}
                          hideMoveToNeedsAttention={false}
                          hidePriceBreakdown={true}
                        />
                      );
                    })}
                  </Box>
                  <Box className={classes.totalCalculation}>
                    <Typography className={classes.calculationText}>
                      {invoice?.totalTripLegs && invoice?.totalTripLegs > 1
                        ? t(
                            'generateInvoice.calculatedTotalInvoiceTripsPlural',
                            {
                              numTrips: invoice?.totalTripLegs,
                              total: invoice?.amount,
                            },
                          )
                        : t(
                            'generateInvoice.calculatedTotalInvoiceTripsSingular',
                            {
                              numTrips: invoice?.totalTripLegs,
                              total: invoice?.amount,
                            },
                          )}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </LocalizationProvider>
        </FullScreenDialogContent>
      </StyledDialog>
    </>
  );
}
